import React, { useState, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence, animate, useMotionValue, useTransform } from "framer-motion";
import {
  Loader2, Home, Sparkles, Plane, Users, AlertCircle, CalendarDays,
  MapPin, Bell, Share2, Copy, Download, Check, Zap, MessageCircle,
} from "lucide-react";
import html2canvas from "html2canvas";
import { PROVINCIAS_AR } from "../lib/content";
import SimulatorStoryCard from "./SimulatorStoryCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SHARE_URL = "https://frenteya.com.ar/#simulador";

// Animated counter
const Counter = ({ to = 0, duration = 1.4 }) => {
  const mv = useMotionValue(0);
  const rounded = useTransform(mv, (v) => Math.round(v));
  const [display, setDisplay] = useState(0);

  React.useEffect(() => {
    const controls = animate(mv, to, { duration, ease: "easeOut" });
    const unsub = rounded.on("change", (v) => setDisplay(v));
    return () => {
      controls.stop();
      unsub();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to]);

  return <span>{display.toLocaleString("es-AR")}</span>;
};

const DIGITS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 0];

const ARRAIGO_OPCIONES = [
  { id: "ninguno", label: "No / Vivo en ciudad grande", sub: "> 20.000 hab." },
  { id: "pueblo_mediano", label: "Ciudad chica", sub: "5.001 a 20.000 hab." },
  { id: "pueblo_pequeno", label: "Pueblo", sub: "hasta 5.000 hab." },
];

export const Simulator = () => {
  const [edad, setEdad] = useState(25);
  const [enPareja, setEnPareja] = useState(false);
  const [dniDigito, setDniDigito] = useState(1);
  const [arraigo, setArraigo] = useState("ninguno");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Lead capture state
  const [leadNombre, setLeadNombre] = useState("");
  const [leadEmail, setLeadEmail] = useState("");
  const [leadProv, setLeadProv] = useState("");
  const [leadConsent, setLeadConsent] = useState(false);
  const [leadLoading, setLeadLoading] = useState(false);
  const [leadDone, setLeadDone] = useState(false);
  const [leadError, setLeadError] = useState("");

  // Share state
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const storyCardRef = useRef(null);

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    setLoading(true);
    setResult(null);
    setLeadDone(false);
    setLeadError("");
    try {
      const { data } = await axios.post(`${API}/simulator/capital-ciudadano`, {
        edad: Number(edad),
        en_pareja: !!enPareja,
        es_argentino: true,
        dni_ultimo_digito: Number(dniDigito),
        arraigo,
      });
      setResult(data);
      setTimeout(() => {
        document.getElementById("sim-result-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    } catch (err) {
      setError("No pudimos calcular tu capital. Probá de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const submitLead = async (e) => {
    e?.preventDefault?.();
    if (!leadConsent) {
      setLeadError("Necesitamos tu consentimiento para avisarte.");
      return;
    }
    setLeadError("");
    setLeadLoading(true);
    try {
      const snapshot = result
        ? `[simulador] ${result.cohorte} · ${result.total_pva.toLocaleString("es-AR")} PVA · ${result.mes_recepcion || "s/f"} · arraigo:${result.arraigo || "ninguno"} · edad:${edad} · dni_dig:${dniDigito}`
        : "";
      await axios.post(`${API}/supporters`, {
        nombre: leadNombre.trim(),
        email: leadEmail.trim().toLowerCase(),
        whatsapp: "",
        provincia: leadProv,
        edad: Number(edad) >= 14 ? Number(edad) : null,
        mensaje: snapshot,
        quiere_voluntariar: false,
        origen: "simulador",
      });
      setLeadDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setLeadError(typeof detail === "string" ? detail : "Revisá tus datos y probá de nuevo.");
    } finally {
      setLeadLoading(false);
    }
  };

  const shareText = () => {
    if (!result) return "";
    if (result.recibe_ya) {
      return `Me toca YA. Por arraigar en el interior me dan ${result.total_pva.toLocaleString("es-AR")} PVA para comprar mi casa. ¿Y a vos? → ${SHARE_URL}`;
    }
    if (result.es_piloto_automatico) {
      return `Cuando cumpla 18 me dan mi Capital Ciudadano (${result.total_pva.toLocaleString("es-AR")} PVA) para comprar mi casa. ¿Y a vos? → ${SHARE_URL}`;
    }
    const mes = result.mes_recepcion || "";
    const ano = result.ano_recepcion ? `del Año ${result.ano_recepcion}` : "";
    return `A mí me toca en ${mes} ${ano}: ${result.total_pva.toLocaleString("es-AR")} PVA para comprar mi casa. ¿Y a vos? → ${SHARE_URL}`;
  };

  const shareWhatsApp = () => {
    const t = encodeURIComponent(shareText());
    window.open(`https://wa.me/?text=${t}`, "_blank");
  };
  const shareTwitter = () => {
    const t = encodeURIComponent(shareText());
    window.open(`https://twitter.com/intent/tweet?text=${t}`, "_blank");
  };
  const shareFacebook = () => {
    const u = encodeURIComponent(SHARE_URL);
    const q = encodeURIComponent(shareText());
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${u}&quote=${q}`, "_blank");
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareText());
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {}
  };

  const downloadStory = async () => {
    if (!storyCardRef.current) return;
    setDownloading(true);
    try {
      // Make the card visible for capture
      storyCardRef.current.style.position = "fixed";
      storyCardRef.current.style.left = "-10000px";
      storyCardRef.current.style.top = "0";
      storyCardRef.current.style.display = "block";
      await new Promise((r) => setTimeout(r, 100));

      const canvas = await html2canvas(storyCardRef.current, {
        backgroundColor: "#0A192F",
        scale: 1,
        width: 1080,
        height: 1920,
        useCORS: true,
        logging: false,
      });
      const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `frente-ya-capital-ciudadano.jpg`;
      a.click();
    } catch (err) {
      console.error(err);
    } finally {
      storyCardRef.current.style.display = "none";
      setDownloading(false);
    }
  };

  return (
    <section
      id="simulador"
      className="relative section-dark py-24 md:py-32 overflow-hidden"
      data-testid="simulator-section"
    >
      <div
        aria-hidden
        className="absolute -top-32 -left-20 w-96 h-96 bg-[#2563EB]/20 blur-3xl rounded-full"
      />
      <div
        aria-hidden
        className="absolute -bottom-32 -right-20 w-96 h-96 bg-[#F5B400]/15 blur-3xl rounded-full"
      />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left: form */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-[#F5B400]" />
              <span className="font-headings text-xs tracking-[0.3em] uppercase text-[#F5B400]">
                Simulador
              </span>
            </div>
            <h2 className="font-headings font-bold uppercase tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
              Calculá tu<br /><span className="text-[#F5B400]">Capital Ciudadano</span>
            </h2>
            <p className="mt-6 text-lg text-white/75 leading-relaxed">
              Cada argentino recibe su patrimonio base según su cohorte de edad y el mes según el último dígito del DNI. Sin crédito, sin cuotas, sin intereses.
            </p>

            <form onSubmit={submit} className="mt-10 space-y-8" data-testid="simulator-form">
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-white/60 mb-2 font-headings">
                  Tu edad actual
                </label>
                <div className="flex items-baseline gap-6">
                  <input
                    type="range"
                    min="0"
                    max="90"
                    value={edad}
                    onChange={(e) => setEdad(Number(e.target.value))}
                    className="flex-1 accent-[#F5B400]"
                    data-testid="simulator-input-edad-range"
                  />
                  <input
                    type="number"
                    min="0"
                    max="120"
                    value={edad}
                    onChange={(e) => setEdad(Number(e.target.value))}
                    className="w-24 bg-transparent border-b-2 border-white/30 focus:border-[#F5B400] outline-none text-3xl font-headings font-bold tabular-nums text-white py-1"
                    data-testid="simulator-input-edad"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-white/60 mb-3 font-headings">
                  Último dígito de tu DNI
                </label>
                <div className="grid grid-cols-10 gap-1.5">
                  {DIGITS.map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setDniDigito(d)}
                      className={`h-10 font-headings font-bold tabular-nums text-base transition-all ${
                        dniDigito === d
                          ? "bg-[#F5B400] text-[#0A192F]"
                          : "border border-white/30 text-white hover:border-[#F5B400]"
                      }`}
                      data-testid={`simulator-dni-${d}`}
                    >
                      {d}
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-xs text-white/50">
                  Define el mes en que cobrás (1→Enero, 2→Febrero ... 0→Octubre).
                </p>
              </div>

              <div>
                <span className="block text-xs uppercase tracking-[0.2em] text-white/60 mb-3 font-headings">
                  ¿Estás casado/a o en pareja?
                </span>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setEnPareja(false)}
                    className={`px-6 py-3 border-2 font-headings uppercase tracking-wide text-sm transition-all ${
                      !enPareja
                        ? "border-[#F5B400] bg-[#F5B400] text-[#0A192F]"
                        : "border-white/30 text-white hover:border-white"
                    }`}
                    data-testid="simulator-pareja-no"
                  >
                    No
                  </button>
                  <button
                    type="button"
                    onClick={() => setEnPareja(true)}
                    className={`px-6 py-3 border-2 font-headings uppercase tracking-wide text-sm transition-all ${
                      enPareja
                        ? "border-[#F5B400] bg-[#F5B400] text-[#0A192F]"
                        : "border-white/30 text-white hover:border-white"
                    }`}
                    data-testid="simulator-pareja-si"
                  >
                    Sí (+25.000 PVA vivienda)
                  </button>
                </div>
              </div>

              {/* ARRAIGO (NEW) */}
              <div>
                <span className="block text-xs uppercase tracking-[0.2em] text-white/60 mb-1 font-headings">
                  <MapPin size={12} className="inline -mt-0.5 mr-1 text-[#F5B400]" />
                  ¿Vivís o te mudarías a un pueblo del interior?
                </span>
                <p className="text-[11px] text-[#F5B400]/90 mb-3 font-headings uppercase tracking-wider">
                  Acceso prioritario por arraigo
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {ARRAIGO_OPCIONES.map((o) => (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => setArraigo(o.id)}
                      className={`text-left px-4 py-3 border-2 font-headings uppercase tracking-wide transition-all ${
                        arraigo === o.id
                          ? "border-[#F5B400] bg-[#F5B400] text-[#0A192F]"
                          : "border-white/25 text-white hover:border-white"
                      }`}
                      data-testid={`simulator-arraigo-${o.id}`}
                    >
                      <div className="text-xs leading-tight">{o.label}</div>
                      <div className={`text-[10px] mt-0.5 ${arraigo === o.id ? "text-[#0A192F]/70" : "text-white/50"}`}>
                        {o.sub}
                      </div>
                    </button>
                  ))}
                </div>
                {arraigo !== "ninguno" && (
                  <p className="mt-2 text-xs text-[#F5B400] flex items-center gap-1">
                    <Zap size={12} /> Te toca <strong>ya</strong>, sin esperar la cohorte.
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="group w-full sm:w-auto bg-[#F5B400] hover:bg-[#d99f00] disabled:opacity-60 text-[#0A192F] font-headings uppercase tracking-wide text-base px-10 py-4 inline-flex items-center justify-center gap-2 transition-all"
                data-testid="simulator-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" size={18} /> Calculando…
                  </>
                ) : (
                  <>
                    Calcular mi capital <Sparkles size={18} />
                  </>
                )}
              </button>
            </form>

            {error && (
              <div className="mt-6 text-sm text-red-300 flex items-center gap-2" data-testid="simulator-error">
                <AlertCircle size={16} /> {error}
              </div>
            )}
          </div>

          {/* Right: result */}
          <div className="lg:col-span-7" data-testid="simulator-result-area">
            <span id="sim-result-anchor" className="block scroll-mt-24" aria-hidden />
            <AnimatePresence mode="wait" initial={false}>
              {!result ? (
                <motion.div
                  key="placeholder"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  className="border border-white/15 bg-white/[0.03] p-8 sm:p-12 min-h-[420px] flex items-center justify-center text-center"
                >
                  <div className="max-w-md">
                    <div className="text-7xl font-headings font-bold text-[#F5B400] mb-4">↑</div>
                    <p className="text-white/70 text-lg">
                      Completá los datos y presioná <strong className="text-white">Calcular</strong> para ver tu patrimonio base y el mes en que lo cobrás.
                    </p>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="result"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.35 }}
                  className="space-y-6"
                  data-testid="simulator-result"
                >
                  {/* RESULT CARD */}
                  <div className="border border-white/15 bg-white/[0.03] p-6 sm:p-10">
                    <div className="flex flex-wrap items-baseline justify-between gap-3 mb-6">
                      <span className={`font-headings text-xs tracking-[0.25em] uppercase ${result.recibe_ya ? "text-[#0A192F] bg-[#F5B400] px-3 py-1" : "text-[#F5B400]"}`}>
                        {result.recibe_ya ? <><Zap size={12} className="inline -mt-0.5 mr-1" />TE TOCA YA</> : result.cohorte}
                      </span>
                      {result.ano_recepcion && !result.recibe_ya ? (
                        <span className="text-white/60 text-sm">
                          Año <strong className="text-white">{result.ano_recepcion}</strong> del programa
                          {result.mes_recepcion ? (
                            <> · <strong className="text-[#F5B400]">{result.mes_recepcion}</strong></>
                          ) : null}
                        </span>
                      ) : null}
                    </div>

                    {result.elegible ? (
                      <>
                        <div className="mb-8">
                          <div className="text-sm uppercase tracking-[0.2em] text-white/60 mb-2 font-headings">
                            Tu patrimonio base total
                          </div>
                          <div className="font-headings text-6xl sm:text-7xl lg:text-8xl font-bold text-white tabular-nums leading-none">
                            <Counter key={result.total_pva} to={result.total_pva} />
                            <span className="text-[#F5B400] text-3xl sm:text-4xl align-top ml-3">PVA</span>
                          </div>
                          <div className="text-white/50 text-sm mt-2">
                            ≈ USD {result.total_pva.toLocaleString("es-AR")}
                          </div>

                          {result.recibe_ya ? (
                            <div className="mt-5 inline-flex items-center gap-2 px-4 py-2 border border-[#F5B400] bg-[#F5B400]/20">
                              <Zap size={16} className="text-[#F5B400]" />
                              <span className="text-sm text-white font-semibold">
                                Acceso inmediato por arraigo
                              </span>
                            </div>
                          ) : result.mes_recepcion ? (
                            <div className="mt-5 inline-flex items-center gap-2 px-4 py-2 border border-[#F5B400]/50 bg-[#F5B400]/10">
                              <CalendarDays size={16} className="text-[#F5B400]" />
                              <span className="text-sm text-white">
                                Recibís tu Capital en <strong className="text-[#F5B400]">{result.mes_recepcion}</strong>
                                {result.es_piloto_automatico ? " del año en que cumplas 18" : ""}
                              </span>
                            </div>
                          ) : null}
                        </div>

                        <div className="grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
                          <Tile icon={<Home size={18} />} label="Vivienda" value={result.base_vivienda_pva} />
                          <Tile icon={<Sparkles size={18} />} label="Equipamiento" value={result.base_equipamiento_pva} />
                          <Tile icon={<Users size={18} />} label="Bonus pareja (vivienda)" value={result.bonus_convivencia_pva} muted={!result.bonus_convivencia_pva} />
                          <Tile icon={<Plane size={18} />} label="Turismo nacional" value={result.bonus_turismo_pva} />
                          {result.arraigo_bonus_pva > 0 && (
                            <Tile icon={<MapPin size={18} />} label="Bonus arraigo" value={result.arraigo_bonus_pva} wide />
                          )}
                        </div>

                        <div className="mt-6 p-4 border-l-2 border-[#F5B400] bg-[#F5B400]/5">
                          <p className="text-white/85 text-sm leading-relaxed">
                            {result.mensaje}
                          </p>
                        </div>

                        {result.es_piloto_automatico && (
                          <div className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#F5B400] font-headings">
                            <span className="h-1.5 w-1.5 rounded-full bg-[#F5B400] animate-pulse" />
                            Régimen estable activado
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="py-6">
                        <p className="font-headings text-2xl text-white uppercase tracking-tight mb-4">
                          {result.cohorte}
                        </p>
                        <p className="text-white/75 leading-relaxed">{result.mensaje}</p>
                      </div>
                    )}
                  </div>

                  {/* LEAD CAPTURE */}
                  {result.elegible && (
                    <motion.div
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3, duration: 0.4 }}
                      className="border-2 border-[#F5B400] bg-[#F5B400]/5 p-6 sm:p-8"
                      data-testid="simulator-lead-capture"
                    >
                      {leadDone ? (
                        <div className="text-center py-4">
                          <Check size={40} className="text-[#F5B400] mx-auto mb-3" />
                          <h3 className="font-headings uppercase tracking-tight text-2xl text-white mb-2">
                            ¡Listo, {leadNombre.split(" ")[0]}!
                          </h3>
                          <p className="text-white/80">
                            Te avisamos {result.recibe_ya ? "cuando se habilite el programa de arraigo" : `cuando arranque tu cohorte${result.mes_recepcion ? ` en ${result.mes_recepcion}` : ""}`}.
                          </p>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-3 mb-4">
                            <Bell size={22} className="text-[#F5B400]" />
                            <h3 className="font-headings uppercase tracking-tight text-xl sm:text-2xl text-white">
                              {result.recibe_ya ? "Avisame cuando se habilite el arraigo" : "Avisame cuando arranque mi cohorte"}
                            </h3>
                          </div>
                          <p className="text-sm text-white/70 mb-5">
                            Te mandamos un email apenas se habilite tu Capital Ciudadano. Sin spam, prometido.
                          </p>
                          <form onSubmit={submitLead} className="grid sm:grid-cols-2 gap-3">
                            <input
                              type="text"
                              required
                              placeholder="Nombre y apellido"
                              value={leadNombre}
                              onChange={(e) => setLeadNombre(e.target.value)}
                              className="bg-black/30 border border-white/20 focus:border-[#F5B400] text-white placeholder-white/40 px-4 py-3 outline-none"
                              data-testid="lead-nombre"
                            />
                            <input
                              type="email"
                              required
                              placeholder="Tu email"
                              value={leadEmail}
                              onChange={(e) => setLeadEmail(e.target.value)}
                              className="bg-black/30 border border-white/20 focus:border-[#F5B400] text-white placeholder-white/40 px-4 py-3 outline-none"
                              data-testid="lead-email"
                            />
                            <select
                              required
                              value={leadProv}
                              onChange={(e) => setLeadProv(e.target.value)}
                              className="bg-black/30 border border-white/20 focus:border-[#F5B400] text-white px-4 py-3 outline-none sm:col-span-2"
                              data-testid="lead-provincia"
                            >
                              <option value="">Elegí tu provincia…</option>
                              {PROVINCIAS_AR.map((p) => (
                                <option key={p} value={p} className="bg-[#0A192F]">{p}</option>
                              ))}
                            </select>
                            <div className="sm:col-span-2 flex items-start gap-2 text-xs text-white/70">
                              <input
                                type="checkbox"
                                id="lead-consent"
                                checked={leadConsent}
                                onChange={(e) => setLeadConsent(e.target.checked)}
                                className="accent-[#F5B400] mt-0.5 shrink-0"
                                data-testid="lead-consent"
                              />
                              <label htmlFor="lead-consent" className="leading-snug">
                                Acepto recibir avisos de Frente Ya sobre el Capital Ciudadano. Puedo darme de baja cuando quiera.
                              </label>
                            </div>
                            <div className="sm:col-span-2 flex flex-col sm:flex-row items-start sm:items-center gap-3">
                              <button
                                type="submit"
                                disabled={leadLoading}
                                className="bg-[#F5B400] hover:bg-[#d99f00] disabled:opacity-60 text-[#0A192F] font-headings uppercase tracking-wide text-sm px-6 py-3 inline-flex items-center justify-center gap-2 transition-colors"
                                data-testid="lead-submit"
                              >
                                {leadLoading ? (
                                  <><Loader2 className="animate-spin" size={16} /> Enviando…</>
                                ) : (
                                  <>Avisame <Bell size={14} /></>
                                )}
                              </button>
                              {leadError && (
                                <span className="text-xs text-red-300 flex items-center gap-1">
                                  <AlertCircle size={14} /> {leadError}
                                </span>
                              )}
                            </div>
                          </form>
                        </>
                      )}
                    </motion.div>
                  )}

                  {/* SHARE */}
                  {result.elegible && (
                    <motion.div
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.45, duration: 0.4 }}
                      className="border border-white/15 bg-white/[0.03] p-6 sm:p-8"
                      data-testid="simulator-share"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <Share2 size={22} className="text-[#F5B400]" />
                        <h3 className="font-headings uppercase tracking-tight text-xl text-white">
                          Compartí tu resultado
                        </h3>
                      </div>
                      <p className="text-sm text-white/70 mb-5">
                        Mandáselo a tu familia y amigos. Que todos sepan cuánto les toca.
                      </p>
                      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                        <ShareBtn onClick={shareWhatsApp} icon={<MessageCircle size={18} />} label="WhatsApp" testid="share-whatsapp" primary />
                        <ShareBtn onClick={shareTwitter} icon={<span className="font-bold text-base">𝕏</span>} label="Twitter / X" testid="share-twitter" />
                        <ShareBtn onClick={shareFacebook} icon={<span className="font-bold text-base">f</span>} label="Facebook" testid="share-facebook" />
                        <ShareBtn onClick={copyLink} icon={copied ? <Check size={18} /> : <Copy size={18} />} label={copied ? "¡Copiado!" : "Copiar"} testid="share-copy" />
                        <ShareBtn onClick={downloadStory} icon={downloading ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />} label={downloading ? "Generando…" : "Historia"} testid="share-story" />
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Tabla de cohortes (referencia) */}
        <div className="mt-16 border border-white/15 bg-white/[0.03] p-6 sm:p-8" data-testid="cohort-reference">
          <div className="font-headings text-xs tracking-[0.3em] uppercase text-[#F5B400] mb-4">
            Calendario de cohortes
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-white/10 border border-white/10">
            {[
              { ano: "Año 1", rango: "Mayores de 62" },
              { ano: "Año 2", rango: "48 a 61" },
              { ano: "Año 3", rango: "35 a 47" },
              { ano: "Año 4", rango: "25 a 34" },
              { ano: "Año 5", rango: "18 a 24" },
              { ano: "Año 6+", rango: "Régimen estable · cumplan 18" },
            ].map((c) => (
              <div key={c.ano} className="bg-[#0A192F] p-4">
                <div className="font-headings text-sm uppercase tracking-wider text-[#F5B400]">{c.ano}</div>
                <div className="text-white text-sm mt-1">{c.rango}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Hidden story card for html2canvas */}
      <div ref={storyCardRef} style={{ display: "none" }}>
        {result && <SimulatorStoryCard result={result} edad={edad} />}
      </div>
    </section>
  );
};

const Tile = ({ icon, label, value, muted, wide }) => (
  <div className={`bg-[#0A192F] p-5 ${muted ? "opacity-50" : ""} ${wide ? "sm:col-span-2" : ""}`}>
    <div className="flex items-center gap-2 text-white/60 text-xs uppercase tracking-[0.2em] font-headings">
      {icon} {label}
    </div>
    <div className="mt-2 font-headings text-2xl font-bold text-white tabular-nums">
      {value.toLocaleString("es-AR")} <span className="text-[#F5B400] text-xs align-top">PVA</span>
    </div>
  </div>
);

const ShareBtn = ({ onClick, icon, label, testid, primary }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex flex-col items-center justify-center gap-1.5 py-3 px-2 border font-headings uppercase tracking-wide text-[11px] transition-all ${
      primary
        ? "bg-[#25D366] hover:bg-[#1dbf5a] border-[#25D366] text-white"
        : "bg-white/5 hover:bg-white/10 border-white/20 hover:border-[#F5B400] text-white hover:text-[#F5B400]"
    }`}
    data-testid={testid}
  >
    {icon}
    <span className="leading-tight">{label}</span>
  </button>
);

export default Simulator;
